"use client";
import CurrencyDisplay from "@/app/components/currencyDisplay";
import HeavyShippingMessage from "@/app/ec/product/[basePath]/heavyShippingMessage";
import NoAdditionalShippingPopover from "@/app/ec/product/[basePath]/noAdditionalShippingPopover";
import { StandardShippingStatus } from "@/app/ec/product/[basePath]/vendor-options/configureVendorOptions";
import { TwoDayDeliveryAbTest } from "@/components/two-day-delivery-badge/twoDayDeliveryAbTest";
import { FunctionComponent } from "react";
import styles from "./priceAndShipping.module.scss";

/**
 * Provides the price and shipping: $#.##/ea + [shipping messaging]
 * @param vendorProduct
 * @param {boolean} [showSavings=true] Determines if savings will be shown.
 * @param {boolean} showShipping Show shipping information
 * @param {boolean} showHeavyShipping Show heavy shipping information
 * @param {boolean} [shortenShippingMessage] Determines if shortened language will be usind with the shipping message.
 * @param {boolean} [showNoAdditionalShippingPopover] whether to show the no additional shipping info popover
 * @param {string} priceClassName pass through for this element's className prop.
 * @param {string} eachClassName pass through for this element's className prop.
 * @param {string} [shippingClassName=""] pass through for this element's className prop.  Make sure to add ml+1 to this if you want a space in front of the +
 * @param {string} [heavyShippingClassName=""] pass through for this element's className prop.  Make sure to add ml+1 to this if you want a space in front of the +
 * @param {"sm" | "md" | "lg"} priceSize "sm" | "md" | "lg"
 * @param activatePDPVendorRowsShippingMessageLengthException Activates a special except for the shipping message, primarily used by the PDP, to shorten the additional standard shipping message but not the free shipping message.
 * @param {boolean} [showAddMoreFreeShipping] whether to show the message to add more for free shipping
 * @param {string} addMoreFreeShippingClassName pass through for this element's className prop.  Make sure to add ml+1 to this if you want a space in front of the +
 */
const PriceAndShipping: FunctionComponent<PriceAndShippingProps> = ({
  vendorOption,
  showShipping,
  showHeavyShipping,
  priceClassName,
  eachClassName,
  hideEach,
  shippingClassName,
  heavyShippingClassName,
  shortenShippingMessage,
  priceSize,
  showNoAdditionalShippingPopover,
  activatePDPVendorRowsShippingMessageLengthException,
  showAddMoreFreeShipping,
  addMoreFreeShippingClassName,
  shippingMessageLineBreak,
  twoDayFlag,
  isVendorPicker,
  twoDayBadgeTooltipPosition,
}) => {
  if (!vendorOption) {
    return null;
  }
  priceClassName = priceClassName ? priceClassName : "";
  eachClassName = eachClassName ? eachClassName : "";
  shippingClassName = shippingClassName ? shippingClassName : "";
  heavyShippingClassName = heavyShippingClassName ? heavyShippingClassName : "";
  const messageTypeKey = shortenShippingMessage === true ? "short" : "full";
  const shippingMessages = {
    full: {
      standard: "Standard Shipping",
      noAdditional: "No Additional Standard Shipping", // Note, per Liz, when this is implemented in the vendor rows, it should be capitalized like this.
      free: (
        <span className={styles.freeShipping}>
          <span className={styles.free}>FREE</span> Standard Shipping
        </span>
      ),
    },
    short: {
      standard: "Std. Shipping",
      noAdditional: "No Additional Std. Shipping", // Note, per Liz, when this is implemented in the vendor rows, it should be capitalized like this.
      free: (
        <span className={styles.freeShipping}>
          <span className={styles.free}>FREE</span> Std. Shipping
        </span>
      ),
    },
  };

  const shippingMessage = (() => {
    if (showShipping === false) {
      return null;
    }
    let message: JSX.Element;
    // The PDP Vendor rows require an exception to the message verbage for short/full.

    if (
      activatePDPVendorRowsShippingMessageLengthException &&
      vendorOption.standardShippingStatus === StandardShippingStatus.NO_ADDITIONAL_SHIPPING
    ) {
      message = <>{shippingMessages["full"].noAdditional}</>;
    } else {
      switch (vendorOption.standardShippingStatus) {
        case StandardShippingStatus.FREE_SHIPPING:
          message = <>{shippingMessages[messageTypeKey].free}</>;
          break;
        case StandardShippingStatus.NO_ADDITIONAL_SHIPPING:
          message = (
            <>
              {shippingMessages[messageTypeKey].noAdditional}
              {showNoAdditionalShippingPopover && <NoAdditionalShippingPopover vendorOption={vendorOption} />}
            </>
          );
          break;
        case StandardShippingStatus.STANDARD_SHIPPING:
        default:
          message = (
            <>
              <CurrencyDisplay value={vendorOption.standardShipping.toString()} />{" "}
              {shippingMessages[messageTypeKey].standard}
            </>
          );
          break;
      }
    }

    return (
      <span className={shippingClassName}>
        {vendorOption?.twoDayDeliverySw || twoDayFlag ? (
          <TwoDayDeliveryAbTest
            message={message}
            isVendorPicker={isVendorPicker}
            twoDayBadgeTooltipPosition={twoDayBadgeTooltipPosition}
          />
        ) : (
          <span>
            <span className={styles.shippingConjunction}>{"+ "}</span>
            {message}
          </span>
        )}
      </span>
    );
  })();

  return (
    <>
      <strong className={`${priceSize} ${priceClassName}`}>
        <CurrencyDisplay value={vendorOption.chosenPriceBreakUnitPrice.toString()} />
      </strong>
      {hideEach ? " " : <span className={eachClassName}>/ea</span>}
      <span className={shippingMessageLineBreak ? "shipping-message-wrapped" : ""}>{shippingMessage}</span>
      {showAddMoreFreeShipping && vendorOption.freeShippingGap > 0 && (
        <span className={addMoreFreeShippingClassName ? addMoreFreeShippingClassName : styles.textSecondary}>
          Add <CurrencyDisplay value={vendorOption.freeShippingGap} /> more for Free Std. Shipping
        </span>
      )}

      {showHeavyShipping && (
        <>
          {" "}
          <HeavyShippingMessage vendorOption={vendorOption} heavyShippingClassName={heavyShippingClassName} />
        </>
      )}
    </>
  );
};

export default PriceAndShipping;

type PriceAndShippingProps = {
  vendorOption?: VendorOptionState;
  showShipping?: boolean;
  showHeavyShipping?: boolean;
  /** Activates a special except for the shipping message, primarily used by the PDP, to shorten the additional standard shipping message but not the free shipping message. */
  activatePDPVendorRowsShippingMessageLengthException?: boolean;
  shortenShippingMessage?: boolean;
  priceClassName?: string;
  eachClassName?: string;
  /** Hide the "/ea" suffix after the price */
  hideEach?: boolean;
  shippingClassName?: string;
  heavyShippingClassName?: string;
  showNoAdditionalShippingPopover?: boolean;
  priceSize: "sm" | "md" | "lg";
  showAddMoreFreeShipping?: boolean;
  addMoreFreeShippingClassName?: string;
  shippingMessageLineBreak?: boolean;
  twoDayFlag?: boolean;
  isVendorPicker?: boolean;
  twoDayBadgeTooltipPosition?: "top" | "bottom";
};
